import Vue from 'vue';

import AssignableAgentsAPI from '../../api/assignableAgents';

const state = {
  records: {},
  uiFlags: {
    isFetching: false,
  },
};

export const types = {
  SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG: 'SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG',
  SET_INBOX_ASSIGNABLE_AGENTS: 'SET_INBOX_ASSIGNABLE_AGENTS',
  APPEND_INBOX_ASSIGNABLE_AGENTS: 'APPEND_INBOX_ASSIGNABLE_AGENTS',
};

// Build the records-map key for a set of inboxes. `perPage` is part of the key
// because pagination pages are only comparable within a fixed page size: two
// consumers hitting the same inbox with different perPage (e.g. the context
// menu at 20 and the assignee dropdown at 10) would otherwise interleave their
// pages into the same entry and produce duplicated or silently skipped agents.
// Legacy single-page callers omit perPage and share the base key.
export const buildRecordKey = (inboxIds, query, perPage) => {
  const ids = Array.isArray(inboxIds) ? inboxIds.join(',') : inboxIds;
  const base = `${ids}:q=${query || ''}`;
  return perPage ? `${base}:pp=${perPage}` : base;
};

export const getters = {
  getAssignableAgents: $state => (inboxIds, query, perPage) => {
    const key = buildRecordKey(inboxIds, query, perPage);
    const entry = $state.records[key];
    if (!entry?.pages) return [];

    // Flatten pages in ascending page order.
    const pages = Object.keys(entry.pages)
      .sort((a, b) => Number(a) - Number(b))
      .map(p => entry.pages[p]);
    const flattened = pages.flat();

    // Dedupe by id (keep first occurrence). Belt-and-suspenders against the
    // same agent surfacing in two adjacent pages on an unstable sort boundary
    // or a brief records-key collision between consumers.
    const seen = new Set();
    return flattened.filter(record => {
      if (!record.confirmed || seen.has(record.id)) return false;
      seen.add(record.id);
      return true;
    });
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  async fetch({ commit }, payload) {
    let inboxIds;
    let perPage = 10;
    let query = '';
    if (Array.isArray(payload)) {
      inboxIds = payload;
    } else if (payload && typeof payload === 'object') {
      inboxIds = payload.inboxIds;
      perPage = payload.perPage || perPage;
      query = payload.query || query;
    } else {
      inboxIds = [];
    }
    commit(types.SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload: members, meta },
      } = await AssignableAgentsAPI.getPage(inboxIds, 1, perPage, query);
      commit(types.SET_INBOX_ASSIGNABLE_AGENTS, {
        key: buildRecordKey(inboxIds, query),
        page: 1,
        members,
        isLastPage: meta ? meta.is_last_page : true,
      });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG, { isFetching: false });
    }
  },

  // Returns whether the fetched page was the last one, so callers walking the
  // full agent list (e.g. the new-conversation form) can loop pages without
  // reaching into the store records for the pagination flag.
  async fetchPage(
    { commit },
    { inboxIds, page = 1, perPage = 10, query = '' }
  ) {
    commit(types.SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG, { isFetching: true });
    const key = buildRecordKey(inboxIds, query, perPage);
    try {
      const {
        data: { payload, meta },
      } = await AssignableAgentsAPI.getPage(inboxIds, page, perPage, query);

      const isLastPage = meta ? meta.is_last_page : true;
      if (page === 1) {
        commit(types.SET_INBOX_ASSIGNABLE_AGENTS, {
          key,
          page: 1,
          members: payload,
          isLastPage,
        });
      } else {
        commit(types.APPEND_INBOX_ASSIGNABLE_AGENTS, {
          key,
          page,
          members: payload,
          isLastPage,
        });
      }
      return isLastPage;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG, { isFetching: false });
    }
  },
};

// Freeze each agent before it enters the store. These records don't mutate
// after load and the assignable-agent list can be large; freezing makes Vue 2
// skip reactive conversion on them, removing getter/setter installation and
// dep-tracking from every access in the render loop (see PRs #894 / #898).
const freezeMembers = members => members.map(member => Object.freeze(member));

export const mutations = {
  [types.SET_INBOX_ASSIGNABLE_AGENTS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_INBOX_ASSIGNABLE_AGENTS]: (
    $state,
    { key, page, members, isLastPage }
  ) => {
    Vue.set($state.records, key, {
      pages: { [page]: freezeMembers(members) },
      isLastPage: !!isLastPage,
    });
  },
  [types.APPEND_INBOX_ASSIGNABLE_AGENTS]: (
    $state,
    { key, page, members, isLastPage }
  ) => {
    const existing = $state.records[key] || { pages: {}, isLastPage: false };
    Vue.set($state.records, key, {
      ...existing,
      pages: { ...existing.pages, [page]: freezeMembers(members) },
      isLastPage: !!isLastPage,
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
